// Shared money and scope helpers for the Opportunities & Dashboard modules.
// Semantic rules (from Feb 2026 spec):
//   • offered_amount = ONLY valid source for portfolio aggregates.
//   • tds_amount     = TDS's share of the offered amount (also valid).
//   • estimated_value = context-only:
//       - Pública -> presupuesto base de licitación (external, auditable).
//       - Privada / TYPSA -> non-contractual internal reference (do NOT show as €).
// Aggregates NEVER include estimated_value or discarded opportunities.

// Single euro formatter used across the whole app. Format: "123.431 €"
// (thousand separator = dot, no decimals, € after the number).
export const formatEuro = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (Number.isNaN(n)) return '-';
  const rounded = Math.round(n);
  // Force grouping so 3456 → "3.456 €" (default es-ES only groups >=10000).
  const parts = new Intl.NumberFormat('es-ES', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).formatToParts(rounded);
  // Manually insert thousand dots when Intl decided not to.
  const abs = Math.abs(rounded).toString();
  const withDots = abs.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const sign = rounded < 0 ? '-' : '';
  // Preserve any locale-defined literals just in case
  void parts;
  return `${sign}${withDots} €`;
};

export const formatEuroShort = (value) => {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.', ',')}M €`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k €`;
  return formatEuro(n);
};

// Scope of an opportunity/company.
// scope is one of 'typsa' | 'privada' | 'publica' (fallback null).
// Callers usually pass opportunity + companyLookup (map id -> company).
// Rule: typsa_org_unit_id present => 'typsa'; else look up company.company_type.
export const getOpportunityScope = (opp, companyLookup) => {
  if (!opp) return null;
  if (opp.typsa_org_unit_id) return 'typsa';
  const cid = opp.company_id;
  if (!cid || !companyLookup) return null;
  const c = companyLookup[cid] || companyLookup.get?.(cid);
  const ct = (c?.company_type || '').toLowerCase();
  if (ct === 'typsa') return 'typsa';
  if (ct === 'publica') return 'publica';
  return c ? 'privada' : null;
};

// Human label / badge classes for a scope.
export const SCOPE_LABEL = { typsa: 'TYPSA', privada: 'Privada', publica: 'Pública' };
export const SCOPE_BADGE_CLS = {
  typsa: 'bg-red-100 text-red-700 border-red-200',
  privada: 'bg-purple-100 text-purple-700 border-purple-200',
  publica: 'bg-green-100 text-green-700 border-green-200',
};

// Display title cascade: short_title -> title truncated to 60 -> '(sin título)'.
export const displayTitle = (opp) => {
  const st = (opp?.short_title || '').trim();
  if (st) return st;
  const t = (opp?.title || opp?.name || '').trim();
  if (!t) return '(sin título)';
  return t.length > 60 ? `${t.slice(0, 60)}…` : t;
};

// Portfolio value used in aggregates/subtotales for a single opportunity.
// Returns { amount, tdsAmount, source }:
//   source = 'offered' | 'estimated_public' | 'none'
// 'estimated_public' is NEVER added to aggregates: callers use it only to render
// a grey "(presupuesto base)" chip on Pública cards.
export const getPortfolioAmount = (opp, scope) => {
  const offered = opp?.offered_amount;
  if (offered !== null && offered !== undefined && offered !== '') {
    const n = Number(offered);
    if (!Number.isNaN(n)) {
      const t = Number(opp.tds_amount);
      return { amount: n, tdsAmount: Number.isNaN(t) ? 0 : t, source: 'offered' };
    }
  }
  if (scope === 'publica' && opp?.estimated_value) {
    const n = Number(opp.estimated_value);
    if (!Number.isNaN(n)) {
      return { amount: null, tdsAmount: 0, source: 'estimated_public', context: n };
    }
  }
  return { amount: null, tdsAmount: 0, source: 'none' };
};

// Compute discount percentage (bajada) automatically. ONLY for Pública.
// Returns a number (%) rounded to 2 decimals, or null when N/A.
export const computeDiscountPercent = (estimated, offered) => {
  const e = Number(estimated);
  const o = Number(offered);
  if (!e || Number.isNaN(e) || Number.isNaN(o)) return null;
  const pct = ((e - o) / e) * 100;
  return Math.round(pct * 100) / 100;
};

// ─── Iter 95: ampliaciones de contrato ──────────────────────────────────
// Suma de todas las ampliaciones de contrato (amount) — 0 si no hay.
export const getExtensionsSum = (opp) => {
  const arr = opp?.contract_extensions;
  if (!Array.isArray(arr) || arr.length === 0) return 0;
  return arr.reduce((acc, e) => acc + (Number(e?.amount) || 0), 0);
};

// Importe FINAL adjudicado a TDS (award_amount + ampliaciones).
// Devuelve null si no hay award_amount.
export const getFinalAwardAmount = (opp) => {
  const base = Number(opp?.award_amount);
  if (Number.isNaN(base) || opp?.award_amount === null || opp?.award_amount === '' || opp?.award_amount === undefined) {
    const ext = getExtensionsSum(opp);
    return ext > 0 ? ext : null;
  }
  return base + getExtensionsSum(opp);
};

// Importe FINAL de TDS. Si tds_amount está definido y award_amount > 0,
// prorratea las ampliaciones por la ratio TDS/total. Si no, cae al total final.
export const getFinalTdsAmount = (opp) => {
  const tds  = Number(opp?.tds_amount);
  const base = Number(opp?.award_amount);
  const ext  = getExtensionsSum(opp);
  const hasTds = !Number.isNaN(tds) && opp?.tds_amount !== null && opp?.tds_amount !== '' && opp?.tds_amount !== undefined;
  if (hasTds && !Number.isNaN(base) && base > 0) {
    return tds + (ext * (tds / base));
  }
  if (hasTds) return tds;
  return getFinalAwardAmount(opp);
};
